/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import ExportIcon from '@mui/icons-material/esm/CloudUpload';
import UsersIcon from '@mui/icons-material/esm/Group';
import HomeIcon from '@mui/icons-material/esm/Home';
import IntegrationIcon from '@mui/icons-material/esm/IntegrationInstructionsOutlined';
import MoreIcon from '@mui/icons-material/esm/MoreHoriz';
import SettingsIcon from '@mui/icons-material/esm/Settings';
import WorkspacesIcon from '@mui/icons-material/esm/Workspaces';
import { Box, Divider, Drawer, ListItemButton, ListItemIcon, Paper, SvgIcon, Tooltip } from '@mui/material';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Path } from '../helper/Path';
import { withUser } from '../UserProvider';

const drawerWidth = 60;

export default withUser(({ user, machines }) => ({
	lruApps: user.lruApps
		.map((machineId) => {
			const machine = machines.find(({ id }) => id === machineId);
			return machine ? { id: machineId, name: machine.name } : undefined;
		})
		.filter((x) => !!x),
	showAccounts: user.rights.includes('account.view'),
	showWorkspaces: user.rights.includes('workspace'),
	showDatabase: user.rights.includes('database')
}))(function StreamSheetNavigation(props) {
	const [adminOpen, setAdminOpen] = useState(false);

	const openTab = (event, url) => {
		if (event.button !== 1) {
			return;
		}

		window.open(`${window.location.origin}${url}`, '_blank');
	};

	const openPage = (url) => {
		props.openPage(url);
	};

	const handleOpenApp = (lru) => {
		props.openPage(Path.app(lru.id));
	};

	const isAdminOpen = () => {
		const href = window.location.href;

		return (
			adminOpen ||
			href.indexOf('/users') !== -1 ||
			href.indexOf('/workspaces') !== -1 ||
			href.indexOf('/settings') !== -1
		);
	};

	return (
		<Drawer
			variant="permanent"
			sx={{
				width: drawerWidth,
				flexShrink: 0,
				[`& .MuiDrawer-paper`]: {
					width: drawerWidth,
					borderRight: (theme) => (theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'),
					boxSizing: 'border-box'
				}
			}}
		>
			<Box sx={{ overflow: 'hidden', marginTop: '51px' }} data-tour="step-nav-bar">
				<List>
					<Tooltip
						placement="right"
						enterDelay={500}
						title={<FormattedMessage id="Navigation.Home" defaultMessage="Home" />}
						key="home"
					>
						<ListItemButton
							sx={{ display: 'block', padding: '6px 4px' }}
							onClick={() => openPage('/home')}
							onMouseDown={(event) => openTab(event, '/home')}
							data-tour="step-nav-bar-home"
						>
							<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
								<HomeIcon />
							</ListItemIcon>
						</ListItemButton>
					</Tooltip>
					<Divider
						key="div1"
						sx={{
							mb: '10px',
							mt: '5px',
							borderColor: (theme) =>
								theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'
						}}
					/>
					<Tooltip
						placement="right"
						enterDelay={500}
						title={<FormattedMessage id="Navigation.Apps" defaultMessage="Apps" />}
						key="apps"
					>
						<ListItemButton
							sx={{ display: 'block', padding: '6px 4px' }}
							onClick={() => openPage('/apps')}
							onMouseDown={(event) => openTab(event, '/apps')}
							data-tour="step-nav-bar-apps"
						>
							<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
								<SvgIcon>
									<path
										fill="currentColor"
										d="M7 2H21C22.11 2 23 2.9 23 4V16C23 17.11 22.11 18 21 18H7C5.9 18 5 17.11 5 16V4C5 2.9 5.9 2 7 2M7 6V10H13V6H7M15 6V10H21V6H15M7 12V16H13V12H7M15 12V16H21V12H15M3 20V6H1V20C1 21.11 1.89 22 3 22H19V20H3Z"
									/>
								</SvgIcon>
							</ListItemIcon>
						</ListItemButton>
					</Tooltip>
					{props.showAccounts ? (
						<Tooltip
							placement="right"
							enterDelay={500}
							title={<FormattedMessage id="Navigation.Accounts" defaultMessage="Accounts" />}
							key="conf"
						>
							<ListItemButton
								sx={{ display: 'block', padding: '6px 4px' }}
								onClick={() => openPage(Path.accounts())}
								onMouseDown={(event) => openTab(event, Path.accounts())}
								data-tour="step-nav-bar-accounts"
							>
								<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
									<SvgIcon viewBox="0 0 56.11 62.6">
										<path
											fill="currentColor"
											stroke="currentColor"
											strokeWidth="1.7px"
											d="M56.88,33.12h-7.17v4.02c-2.36,0-4.59,.07-6.81-.03-1.35-.06-2.17-1.16-2.21-2.68-.03-1.39,0-2.78,0-4.24H4.19v16.2c5.91-1.33,11.91-.4,17.86-.68,.51-.02,1.02,0,1.67,0,0-1.7,0-3.25,0-4.8,.01-1.91,.89-2.79,2.78-2.8,2.04-.01,4.07,0,6.22,0v3.96h6.92v3h-6.79v3.98h6.78v3.07h-6.87v4.01c-2.37,0-4.6,.06-6.83-.02-1.29-.05-2.16-1.09-2.22-2.4-.03-.64,0-1.28,0-1.92,0-.63,0-1.27,0-2.01-.47-.03-.86-.07-1.24-.07-4.56,0-9.12,0-13.68,0-3.32,0-4.67,1.35-4.68,4.64,0,2.84-.03,5.68-.04,8.52H0V13.2c.05-.06,.13-.12,.13-.19,.41-4.87,4.62-7.2,8.81-7.08,4.47,.13,8.94,.03,13.41,.03,.42,0,.84,0,1.37,0,0-.93,.07-1.69-.01-2.43-.19-1.67,.43-2.85,1.97-3.53h6.96l.21,4.11h6.79v3.1h-6.82v3.89h6.82v3.09h-6.92v3.93c-2,0-3.83,0-5.67,0-2.62,0-3.33-.71-3.33-3.33,0-1.58,0-3.16,0-4.77-5.48,0-10.75-.1-16.02,.04-2.37,.06-3.55,1.58-3.57,3.96-.02,2.52,0,5.04,0,7.56,0,1.47,0,2.93,0,4.46H40.68c0-1.46-.03-2.85,0-4.24,.04-1.57,.81-2.59,2.2-2.65,2.23-.1,4.46-.03,6.83-.03v3.95c1.21,0,2.29-.03,3.36,0,1.27,.04,2.53,.13,3.8,.19v2.88h-7.08v4.08h7.08v2.88Z"
										/>
									</SvgIcon>
								</ListItemIcon>
							</ListItemButton>
						</Tooltip>
					) : null}
					{props.showAccounts ? (
						<Tooltip
							enterDelay={500}
							placement="right"
							title={
								<FormattedMessage
									id="Navigation.IntegrationAccounts"
									defaultMessage="Integration Accounts"
								/>
							}
							key="integrations"
						>
							<ListItemButton
								sx={{ display: 'block', padding: '6px 4px' }}
								onClick={() => openPage(Path.integrations())}
								onMouseDown={(event) => openTab(event, Path.integrations())}
								data-tour="step-nav-bar-integrations"
							>
								<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
									<IntegrationIcon />
								</ListItemIcon>
							</ListItemButton>
						</Tooltip>
					) : null}
					<Tooltip
						enterDelay={500}
						placement="right"
						title={<FormattedMessage id="Navigation.Export" defaultMessage="Export" />}
						key="export"
					>
						<ListItemButton
							sx={{ display: 'block', padding: '6px 4px' }}
							onClick={() => openPage(Path.export(props.appId))}
							onMouseDown={(event) => openTab(event, Path.export(props.appId))}
							data-tour="step-nav-bar-export"
						>
							<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
								<ExportIcon />
							</ListItemIcon>
						</ListItemButton>
					</Tooltip>
				</List>
				<Divider
					key="div2"
					sx={{
						mb: '10px',
						mt: '0px',
						borderColor: (theme) =>
							theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'
					}}
				/>
				{props.lruApps.map((lru, index) => (
					<Tooltip
						placement="right"
						enterDelay={500}
						title={<FormattedMessage id="Navigation.App" defaultMessage="App" />}
						key={`${lru.id}`}
					>
						<ListItemButton
							sx={{ display: 'block', padding: '6px 4px' }}
							disabled={props.appLoadingDisabled}
							onClick={() => handleOpenApp(lru)}
							onMouseDown={(event) => openTab(event, Path.app(lru.id))}
							data-tour={index === 0 ? 'nav-bar-app' : undefined}
						>
							<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
								<SvgIcon>
									<path
										fill="currentColor"
										d="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z"
									/>
								</SvgIcon>
							</ListItemIcon>
							<Typography
								style={{
									textAlign: 'center',
									fontSize: '7pt',
									overflow: 'hidden',
									textOverflow: 'ellipsis'
								}}
							>
								{lru ? lru.name : ''}
							</Typography>
						</ListItemButton>
					</Tooltip>
				))}
				<Paper
					sx={{
						position: 'absolute',
						bottom: '0px',
						width: '59px'
					}}
					data-tour="step-nav-bar-admin"
				>
					{isAdminOpen()
						? [
								<Divider
									key="div3"
									sx={{
										my: '5px',
										borderColor: (theme) =>
											theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'
									}}
								/>,
								<Tooltip
									placement="right"
									enterDelay={500}
									title={<FormattedMessage id="Navigation.Users" defaultMessage="Users" />}
									key="user"
								>
									<ListItemButton
										sx={{ display: 'block', padding: '6px 4px' }}
										onClick={() => openPage('/users')}
										onMouseDown={(event) => openTab(event, '/users')}
									>
										<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
											<UsersIcon />
										</ListItemIcon>
									</ListItemButton>
								</Tooltip>
						  ]
						: null}
					{isAdminOpen() && props.showWorkspaces ? (
						<Tooltip
							placement="right"
							enterDelay={500}
							title={<FormattedMessage id="Navigation.Workspaces" defaultMessage="Workspaces" />}
							key="workspace"
						>
							<ListItemButton
								sx={{ display: 'block', padding: '6px 4px' }}
								onClick={() => openPage('/workspaces')}
								onMouseDown={(event) => openTab(event, '/workspaces')}
							>
								<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
									<WorkspacesIcon />
								</ListItemIcon>
							</ListItemButton>
						</Tooltip>
					) : null}
					{isAdminOpen() ? (
						<Tooltip
							placement="right"
							enterDelay={500}
							title={<FormattedMessage id="Navigation.Settings" defaultMessage="Settings" />}
							key="settings"
						>
							<ListItemButton
								sx={{ display: 'block', padding: '6px 4px' }}
								onClick={() => openPage('/settings')}
								onMouseDown={(event) => openTab(event, '/settings')}
							>
								<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
									<SettingsIcon />
								</ListItemIcon>
							</ListItemButton>
						</Tooltip>
					) : null}
					<Divider
						key="div4"
						sx={{
							my: '5px',
							borderColor: (theme) =>
								theme.palette.mode === 'dark' ? '1px solid #47525d' : '1px solid #ececef'
						}}
					/>
					<List sx={{ paddingTop: '2px' }}>
						<Tooltip
							enterDelay={500}
							placement="right"
							title={<FormattedMessage id="Navigation.Admin" defaultMessage="Admin" />}
							key="admin"
						>
							<ListItemButton
								sx={{ display: 'block', padding: '6px 4px' }}
								onClick={() => setAdminOpen(!adminOpen)}
							>
								<ListItemIcon style={{ padding: '3px 15px', minWidth: '0px' }}>
									<MoreIcon />
								</ListItemIcon>
							</ListItemButton>
						</Tooltip>
					</List>
				</Paper>
			</Box>
		</Drawer>
	);
});
